import React from "react"

import { CodeBlock } from "@javascript/components/ui/code-block"
import {
  TypographyH2,
  TypographyH3,
  TypographyInlineCode,
  TypographyList,
  TypographyP
} from "@javascript/components/ui/typography"

interface TemplateAndBlocksPageProps {
  templateLiquidExample: string
}

export function TemplateAndBlocksPage({ templateLiquidExample }: TemplateAndBlocksPageProps) {
  return (
    <div className="space-y-8">
      <div>
        <TypographyH2>template.liquid & Block Slots Reference</TypographyH2>
        <TypographyP>
          Your theme's <TypographyInlineCode>template.liquid</TypographyInlineCode> is a free-form HTML layout
          with CSS styling. The platform renders wedding content and workflow behavior through <strong>blocks</strong> —
          you place them using a simple Liquid tag. Curated contract v3 themes may separately add presentational
          interactivity through a manifest-declared runtime.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Block-slot placement syntax</TypographyH3>
        <TypographyP>
          Use the <TypographyInlineCode>{`{% block "id" %}`}</TypographyInlineCode> tag to place a block instance
          in your layout:
        </TypographyP>
        <CodeBlock
          code={`<section class="invite__hero">
  {% block "hero" %}
</section>

<section class="invite__story">
  {% block "story" %}
</section>`}
          language="liquid"
          className="my-4"
        />
        <TypographyP className="text-sm text-muted-foreground">
          The id you pass to <TypographyInlineCode>{`{% block %}`}</TypographyInlineCode> must match
          one of the instance ids declared in your <TypographyInlineCode>theme.json</TypographyInlineCode>.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Real example from the Elegant theme</TypographyH3>
        <CodeBlock code={templateLiquidExample} language="liquid" className="my-4" />
        <TypographyP className="text-sm text-muted-foreground">
          Each block slot renders the platform's content and behavior for that block instance. The theme controls
          layout, styling, and optional client-side presentation; the platform remains responsible for content,
          validation, persistence, and security-sensitive workflows.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>The Liquid sandbox: what's allowed</TypographyH3>
        <TypographyP>
          Template rendering runs in a restricted sandbox for safety and performance. Here are the key restrictions:
        </TypographyP>
      </div>

      <div>
        <TypographyH3>No file inclusion or script tags</TypographyH3>
        <TypographyP>
          Templates <strong>cannot</strong> include arbitrary files or emit script tags:
        </TypographyP>
        <TypographyList>
          <li>
            No <TypographyInlineCode>include</TypographyInlineCode> or{" "}
            <TypographyInlineCode>render</TypographyInlineCode> tags for external files
          </li>
          <li>No <TypographyInlineCode>&lt;script&gt;</TypographyInlineCode> tags</li>
          <li>No inline event handlers like <TypographyInlineCode>onclick</TypographyInlineCode></li>
        </TypographyList>
        <TypographyP className="text-sm text-muted-foreground mt-4">
          JavaScript never enters through Liquid. If a curated theme needs it, declare one trusted runtime in
          <TypographyInlineCode>theme.json</TypographyInlineCode>. The platform loads that entrypoint after its
          allowlisted dependencies, giving reviewers one explicit loading path.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Content is escaped by default</TypographyH3>
        <TypographyP>
          Your template never reads couple content directly — it only places{" "}
          <TypographyInlineCode>{`{% block "id" %}`}</TypographyInlineCode> slots. The platform renders each
          block's content <strong>server-side and HTML-escaped by default</strong> before it reaches that slot:
        </TypographyP>
        <TypographyList>
          <li>
            Couple-typed values are HTML-escaped by the platform's block renderers, preventing injection of
            arbitrary HTML
          </li>
          <li>
            Only <TypographyInlineCode>richtext</TypographyInlineCode> fields can emit safe HTML, and only
            after server-side sanitization
          </li>
          <li>
            There is no escape hatch: your template has no way to reference raw couple content or bypass this
            escaping
          </li>
        </TypographyList>
        <TypographyP className="text-sm text-muted-foreground mt-4">
          This protects every invitation from a compromised couple account. An optional runtime can enhance the
          rendered DOM, but it does not become a second path for loading or persisting invitation content.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>What Liquid you can actually use</TypographyH3>
        <TypographyP>
          The sandbox is intentionally minimal — your template has access to exactly three tags, and no filters:
        </TypographyP>
        <TypographyList>
          <li>
            <TypographyInlineCode>{`{% block "id" %}`}</TypographyInlineCode> — place a block instance
          </li>
          <li>
            <TypographyInlineCode>{`{% asset_url 'filename' %}`}</TypographyInlineCode> — resolve a URL to a
            file in your theme's <TypographyInlineCode>assets/</TypographyInlineCode> folder
          </li>
          <li>
            <TypographyInlineCode>{`{% platform_head %}`}</TypographyInlineCode> — a required slot inside{" "}
            <TypographyInlineCode>&lt;head&gt;</TypographyInlineCode> where the platform inserts its own markup
          </li>
        </TypographyList>
        <TypographyP className="text-sm text-muted-foreground mt-4">
          There is no <TypographyInlineCode>if</TypographyInlineCode>,{" "}
          <TypographyInlineCode>for</TypographyInlineCode>, or any other control-flow tag, and{" "}
          <TypographyInlineCode>{`{{ }}`}</TypographyInlineCode> output cannot use filters (e.g.{" "}
          <TypographyInlineCode>{`{{ x | upcase }}`}</TypographyInlineCode>) — any filter is rejected. Your
          layout is markup and CSS placing block slots, not logic.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Why these restrictions exist</TypographyH3>
        <TypographyP>
          These sandbox rules keep curated theme packages constrained and reviewable:
        </TypographyP>
        <TypographyList>
          <li>
            <strong>No file access</strong> — themes cannot read system files or access the database
          </li>
          <li>
            <strong>One runtime loading path</strong> — optional JavaScript and allowlisted dependencies are declared
            in <TypographyInlineCode>theme.json</TypographyInlineCode>, never hidden in Liquid
          </li>
          <li>
            <strong>Content is safe by default</strong> — escaped couple content cannot contain injection attacks
          </li>
          <li>
            <strong>Performance & stability</strong> — resource limits prevent runaway loops or memory exhaustion
          </li>
        </TypographyList>
      </div>
    </div>
  )
}
